import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * Workflow Funnel Component
 * Visual representation of announcement workflow stages
 */
export default function WorkflowFunnel({
  drafts = 0,
  pending = 0,
  published = 0,
  isLoading = false,
}) {
  // Debug log
  console.log('WorkflowFunnel props:', { drafts, pending, published });
  
  const stages = useMemo(() => {
    const total = drafts + pending + published;
    console.log('WorkflowFunnel total:', total);
    if (total === 0) return [];

    return [
      {
        label: 'Créées',
        count: total,
        percentage: 100,
        color: 'bg-gray-400',
        description: 'Total des annonces',
      },
      {
        label: 'Soumises',
        count: pending + published,
        percentage: total > 0 ? Math.round(((pending + published) / total) * 100) : 0,
        color: 'bg-blue-500',
        description: 'En cours de traitement',
      },
      {
        label: 'Publiées',
        count: published,
        percentage: total > 0 ? Math.round((published / total) * 100) : 0,
        color: 'bg-green-500',
        description: 'Complétées avec succès',
      },
    ];
  }, [drafts, pending, published]);

  if (isLoading) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const total = drafts + pending + published;

  if (total === 0) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <h5 className="text-xs sm:text-sm font-semibold text-gray-700 mb-3">Suivi des annonces</h5>
        <div className="flex flex-col items-center justify-center py-8 text-gray-400">
          <svg className="w-12 h-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <p className="text-sm">Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 bg-white rounded-lg border border-gray-200">
      <h5 className="text-sm font-semibold text-gray-700 mb-4">Suivi des annonces</h5>
      
      <div className="space-y-2 sm:space-y-3">
        {stages.map((stage, index) => (
          <div key={stage.label} className="relative">
            <div key={index} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm font-medium text-gray-700">{stage.label}</span>
                <span className="text-xs sm:text-sm font-semibold text-gray-900">{stage.count}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-1.5 sm:h-2 overflow-hidden">
                  <div
                    className={`h-full ${stage.color} rounded-r-full transition-all duration-500 flex items-center justify-end pr-3`}
                    style={{ width: `${stage.percentage}%` }}
                  >
                    {stage.percentage > 15 && (
                      <span className="text-xs font-bold text-white">
                        {stage.count}
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-12 text-right">
                  <span className="text-[10px] sm:text-xs text-gray-500 min-w-[2.5rem] sm:min-w-[3rem] text-right">{stage.percentage}%</span>
                </div>
              </div>
              
              {/* Connector line */}
              {index < stages.length - 1 && (
                <div className="ml-20 pl-3 h-2 border-l-2 border-dashed border-gray-300" />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Conversion insight */}
      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 rounded-lg">
        <p className="text-[10px] sm:text-xs text-blue-700">Taux de publication global</p>
        <div className="flex items-center justify-between">
          {/* <p className="text-[10px] sm:text-xs text-gray-500">Créées → Publiées</p> */}
          <span className={`font-semibold ${
            stages[2]?.percentage >= 80 ? 'text-green-600' :
            stages[2]?.percentage >= 50 ? 'text-amber-600' : 'text-red-600'
          }`}>
            {stages[2]?.percentage || 0}%
          </span>
        </div>
      </div>
    </div>
  );
}

WorkflowFunnel.propTypes = {
  drafts: PropTypes.number,
  pending: PropTypes.number,
  published: PropTypes.number,
  isLoading: PropTypes.bool,
};
